/**
 * Alert Component
 * 
 * A versatile alert component for displaying messages, notifications,
 * and feedback to users with various severity levels.
 */
import * as React from 'react'
import { cn } from '../../utils/tailwind-utils'

interface AlertProps {
  /**
   * The content to display inside the alert
   */
  children: React.ReactNode
  
  /**
   * The title of the alert
   */
  title?: string
  
  /**
   * The severity level of the alert
   */
  variant?: 'info' | 'success' | 'warning' | 'error' | 'default'
  
  /**
   * Additional CSS classes
   */
  className?: string
  
  /**
   * Optional icon to display before the content
   */
  icon?: React.ReactNode
  
  /**
   * Whether the alert is dismissible
   */
  dismissible?: boolean
  
  /**
   * Function to call when the alert is dismissed
   */
  onDismiss?: () => void
}

/**
 * Alert component for displaying messages and notifications
 * 
 * @example
 * // Basic usage
 * <Alert>This is a default alert</Alert>
 * 
 * @example
 * // With title and variant
 * <Alert title="Success!" variant="success">
 *   Your changes have been saved successfully.
 * </Alert>
 * 
 * @example
 * // Dismissible alert
 * <Alert 
 *   variant="error" 
 *   title="Error" 
 *   dismissible 
 *   onDismiss={() => setShowError(false)}
 * >
 *   There was a problem processing your request.
 * </Alert>
 */
function Alert({
  children,
  title,
  variant = 'default',
  className,
  icon,
  dismissible = false,
  onDismiss,
}: AlertProps) {
  // Variant-specific styles
  const variantStyles = {
    default: 'bg-card border-border text-card-foreground',
    info: 'bg-info/10 border-info text-info-foreground',
    success: 'bg-success/10 border-success text-success-foreground',
    warning: 'bg-warning/10 border-warning text-warning-foreground',
    error: 'bg-destructive/10 border-destructive text-destructive-foreground',
  }
  
  // Default icons based on variant
  const getDefaultIcon = () => {
    if (!icon) return null
    
    switch (variant) {
      case 'info':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
        )
      case 'success':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        )
      case 'warning':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        )
      case 'error':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
        )
      default:
        return null
    }
  }
  
  const defaultIcon = getDefaultIcon()
  
  return (
    <div
      className={cn(
        'relative border rounded-lg p-4',
        variantStyles[variant],
        className
      )}
      role="alert"
    >
      <div className="flex">
        {/* Icon */}
        {(icon || defaultIcon) && (
          <div className="flex-shrink-0 mr-3">
            {icon || defaultIcon}
          </div>
        )}
        
        {/* Content */}
        <div className="flex-1">
          {title && (
            <h3 className="text-base font-medium mb-1">{title}</h3>
          )}
          <div className={cn(!title && 'text-sm')}>
            {children}
          </div>
        </div>
        
        {/* Dismiss button */}
        {dismissible && onDismiss && (
          <button
            type="button"
            className="ml-auto -mx-1.5 -my-1.5 rounded-lg p-1.5 inline-flex h-8 w-8 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background focus:ring-border"
            onClick={onDismiss}
            aria-label="Dismiss"
          >
            <span className="sr-only">Dismiss</span>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              className="w-5 h-5"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}

export default Alert
